/**
 * lib/format-datetime.ts — THE canonical date/time DISPLAY formatters.
 *
 * Before this existed (2026-08-27, OPEN_IMPROVEMENTS row 52) the repo had 21
 * locally-defined per-file formatters and 156 raw `toLocale*String` calls
 * disagreeing on locale (en-US / en-CA / undefined), empty sentinel ("—" /
 * "Not set" / "-" / "" / echo-the-iso), and — the correctness half — NONE
 * passed a timeZone, so every one rendered in the DEVICE clock's zone. On a
 * correctly-provisioned tablet that happens to match; on a mis-set tablet or
 * any server render it silently shows the wrong hour (the P0-1 bug class).
 *
 * Rules:
 *   - Every formatter pins `timeZone: getDiningTimezone()` — output is
 *     device-TZ-invariant by construction (the unit tests prove it: they
 *     assert exact strings and pass under any host TZ).
 *   - ONE locale: "en-US", chosen for rendering style, not nationality —
 *     "Aug 27, 2026, 2:30 PM" (en-CA renders "Aug. 27, 2026, 2:30 p.m.",
 *     which is noisier on dense tablet UI and on printed forms). Changing
 *     the app-wide style is a one-line edit HERE, nowhere else.
 *   - ONE empty/invalid sentinel: "—" (em dash — matches table conventions).
 *   - Date-ONLY strings ("YYYY-MM-DD", e.g. meal_date) go through
 *     formatDiningDateOnly, which never constructs a Date at all — a bare
 *     date has no instant, so no TZ math may touch it (ADR 0007 spirit).
 *
 * This module is DISPLAY only. Day-bucketing/query bounds stay in
 * lib/meal-date.ts (getMealDateToday, getDiningDayWindowUTC, …).
 */

import { getDiningTimezone } from "@/lib/meal-date"

export const EMPTY_DATE_DISPLAY = "—"

const LOCALE = "en-US"

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
]

/**
 * Parse into a Date safely: null for null/undefined/empty/invalid input.
 * Exported for callers that need validity before formatting (e.g. the
 * <RelativeTime> component's dateTime attribute).
 */
export function toValidDate(iso: string | number | Date | null | undefined): Date | null {
  if (iso === null || iso === undefined || iso === "") return null
  const d = iso instanceof Date ? iso : new Date(iso)
  return isNaN(d.getTime()) ? null : d
}

/** "2:30 PM" — time of the instant, dining timezone. */
export function formatDiningTime(iso: string | number | Date | null | undefined): string {
  const d = toValidDate(iso)
  if (!d) return EMPTY_DATE_DISPLAY
  return d.toLocaleTimeString(LOCALE, { hour: "numeric", minute: "2-digit", timeZone: getDiningTimezone() })
}

/** "Aug 27, 2026" — calendar date of the instant, dining timezone. */
export function formatDiningDate(iso: string | number | Date | null | undefined): string {
  const d = toValidDate(iso)
  if (!d) return EMPTY_DATE_DISPLAY
  return d.toLocaleDateString(LOCALE, { year: "numeric", month: "short", day: "numeric", timeZone: getDiningTimezone() })
}

/** "August 27, 2026" — long month, for printed documents. */
export function formatDiningDateLong(iso: string | number | Date | null | undefined): string {
  const d = toValidDate(iso)
  if (!d) return EMPTY_DATE_DISPLAY
  return d.toLocaleDateString(LOCALE, { year: "numeric", month: "long", day: "numeric", timeZone: getDiningTimezone() })
}

/** "Aug 27, 2026, 2:30 PM" — date + time, dining timezone. */
export function formatDiningDateTime(iso: string | number | Date | null | undefined): string {
  const d = toValidDate(iso)
  if (!d) return EMPTY_DATE_DISPLAY
  return d.toLocaleString(LOCALE, {
    year: "numeric", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
    timeZone: getDiningTimezone(),
  })
}

/** "Wednesday, August 27, 2026, 2:30 PM" — for incident prints and formal documents. */
export function formatDiningWeekdayDateTime(iso: string | number | Date | null | undefined): string {
  const d = toValidDate(iso)
  if (!d) return EMPTY_DATE_DISPLAY
  return d.toLocaleString(LOCALE, {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
    hour: "numeric", minute: "2-digit",
    timeZone: getDiningTimezone(),
  })
}

/**
 * Format a USER-ENTERED calendar date — date_of_birth, move_in_date,
 * contract/insurance dates, sick/away windows: "Aug 27, 1947".
 *
 * Decision rule: was the value TYPED as a date by a human? → this function.
 * Was it CAPTURED as a moment (ordered_at, served_at, check_date)? → the
 * formatDining* instant formatters above.
 *
 * Why this exists (Vercel agent review on PR #447, verified): date inputs
 * store either bare "YYYY-MM-DD" or a midnight-UTC instant
 * ("1947-08-27T00:00:00.000Z"). Formatting that instant in the dining zone
 * renders Aug 26 — one day early, every time. A calendar date has no zone,
 * so it renders as its own UTC calendar day: bare strings go through the
 * Date-free path, instants are formatted with timeZone:"UTC".
 */
export function formatCalendarDate(
  value: string | null | undefined,
  style: "short" | "long" = "short",
): string {
  if (!value) return EMPTY_DATE_DISPLAY
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatDiningDateOnly(value, style)
  const d = toValidDate(value)
  if (!d) return EMPTY_DATE_DISPLAY
  return d.toLocaleDateString(LOCALE, {
    year: "numeric", month: style === "long" ? "long" : "short", day: "numeric",
    timeZone: "UTC",
  })
}

/**
 * Format a MIXED-provenance date column — one where some writers store
 * midnight-UTC calendar dates (human-typed date inputs, e.g. manual ledger
 * posts, sick/away edits) and others store real capture instants (recurring
 * billing, ISD delivery charges, away-list creates before 2026-08-27).
 *
 * Shape decides: bare "YYYY-MM-DD" and exact-midnight-UTC values are calendar
 * dates and render as their own UTC day; anything else is a real instant and
 * renders in the dining zone. A genuine instant landing on exactly
 * T00:00:00.000Z is a sub-millisecond coincidence — the ambiguity is inherent
 * to the mixed stored shape, and the durable fix is write-path normalization
 * (OPEN_IMPROVEMENTS row 71), not more formatter cleverness.
 */
export function formatMixedDate(
  value: string | null | undefined,
  style: "short" | "long" = "short",
): string {
  if (!value) return EMPTY_DATE_DISPLAY
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatDiningDateOnly(value, style)
  if (/T00:00:00(\.000)?Z$/.test(value)) return formatCalendarDate(value, style)
  return style === "long" ? formatDiningDateLong(value) : formatDiningDate(value)
}

/** "Wed, Aug 27, 2:30 PM" — near-term event datetimes (activity/bus boards), where the weekday carries the signal and the year is noise. */
export function formatDiningEventDateTime(iso: string | number | Date | null | undefined): string {
  const d = toValidDate(iso)
  if (!d) return EMPTY_DATE_DISPLAY
  return d.toLocaleString(LOCALE, {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
    timeZone: getDiningTimezone(),
  })
}

/**
 * Format a date-ONLY string ("YYYY-MM-DD" — meal_date, order_date shapes)
 * WITHOUT constructing a Date: "Aug 27, 2026". A bare date is not an instant;
 * running it through `new Date()` re-introduces the exact midnight-UTC
 * day-shift this module exists to kill. Anything else returns the sentinel.
 */
export function formatDiningDateOnly(dateStr: string | null | undefined, style: "short" | "long" = "short"): string {
  if (!dateStr) return EMPTY_DATE_DISPLAY
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr)
  if (!m) return EMPTY_DATE_DISPLAY
  const [, y, mo, day] = m
  const monthIdx = Number(mo) - 1
  if (monthIdx < 0 || monthIdx > 11) return EMPTY_DATE_DISPLAY
  // Reject impossible days ("2026-02-30", "2026-08-00") by round-tripping the
  // day through a UTC-noon Date: an out-of-range day rolls to a different
  // calendar day, so the parts no longer match. Noon can't cross a date line,
  // and the check never touches device/dining TZ.
  const probe = new Date(`${dateStr}T12:00:00Z`)
  if (
    isNaN(probe.getTime()) ||
    probe.getUTCFullYear() !== Number(y) ||
    probe.getUTCMonth() !== monthIdx ||
    probe.getUTCDate() !== Number(day)
  ) {
    return EMPTY_DATE_DISPLAY
  }
  const months = style === "long" ? MONTHS_LONG : MONTHS_SHORT
  return `${months[monthIdx]} ${Number(day)}, ${y}`
}

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

/** "May 11" — year-less short date, only for formatRelative's same-year fallback. */
function formatDiningMonthDay(d: Date): string {
  return d.toLocaleDateString(LOCALE, { month: "short", day: "numeric", timeZone: getDiningTimezone() })
}

/**
 * Short relative timestamp for activity/audit surfaces (folded in from the
 * old lib/relative-time.ts, whose name collided with
 * components/shared/relative-time.tsx and hid it from discovery):
 *   < 1 minute  → "just now"      < 1 hour → "Nm ago"
 *   < 24 hours  → "Nh ago"        < 7 days → "Nd ago"
 *   same year   → "May 11"        older    → "May 11, 2026"
 * Future values (beyond clock skew) render absolute with time — scheduled
 * items want the date, not "in 3 hours". Absolute fallbacks are dining-TZ
 * pinned (the old module formatted them in the device zone).
 * Empty/invalid input returns "" (not the em dash): these render inline
 * after words like "Updated", where a dangling "—" reads as punctuation.
 */
export function formatRelative(
  value: string | number | Date | null | undefined,
  now: Date = new Date(),
): string {
  const d = toValidDate(value)
  if (!d) return ""

  const deltaMs = now.getTime() - d.getTime()
  if (deltaMs < -MINUTE_MS) return formatDiningDateTime(d)
  if (deltaMs < MINUTE_MS) return "just now"
  if (deltaMs < HOUR_MS) return `${Math.floor(deltaMs / MINUTE_MS)}m ago`
  if (deltaMs < DAY_MS) return `${Math.floor(deltaMs / HOUR_MS)}h ago`
  if (deltaMs < 7 * DAY_MS) return `${Math.floor(deltaMs / DAY_MS)}d ago`
  return d.getFullYear() === now.getFullYear() ? formatDiningMonthDay(d) : formatDiningDate(d)
}

/**
 * "Wednesday, August 27, 2026" from a date-ONLY string. The weekday genuinely
 * needs calendar math, so the day is anchored at UTC noon and formatted in
 * UTC — noon can't cross a date line in any zone, so the weekday is exact
 * and the device/dining TZ never enters.
 */
export function formatDiningWeekdayDateOnly(dateStr: string | null | undefined): string {
  if (!dateStr) return EMPTY_DATE_DISPLAY
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return EMPTY_DATE_DISPLAY
  const d = new Date(`${dateStr}T12:00:00Z`)
  if (isNaN(d.getTime())) return EMPTY_DATE_DISPLAY
  return d.toLocaleDateString(LOCALE, { weekday: "long", year: "numeric", month: "long", day: "numeric", timeZone: "UTC" })
}
